import {getReactRoot} from 'utils/dom';
import {
    EmployeeRightsCreateBackendData,
    EmployeeRightsEditBackendData,
    GranularRight,
    UIElement,
    UIRenderableElement,
} from './types';

export const createInitialValues = ({
    salesMgmtLicense,
    timeTracking,
    uiElements,
    userLicense,
}:
    | EmployeeRightsCreateBackendData['data']
    | EmployeeRightsEditBackendData['data']) => {
    const rights: Record<string, Record<string, boolean>> = {};
    uiElements.forEach((element) => {
        if (element.type === 'granular_right') {
            const o: Record<string, boolean> = {};
            element.actions.forEach((action) => {
                o[action.name] = action.value;
            });
            rights[element.name] = o;
        }
    });

    return {
        rights,
        sales_mgmt_license: salesMgmtLicense,
        time_tracking: timeTracking,
        user_license: userLicense.initialValue,
    };
};

export const getCheckboxes = (htmlElement: Element) => [
    ...htmlElement.querySelectorAll('input[type="checkbox"]'),
];

export const getAttribute = (attribute: string) => (element: Element) =>
    element.getAttribute(attribute);

export const isRenderableElement = (
    element: UIElement,
): element is UIRenderableElement => element.type !== 'section';

export const isInSection =
    (section: string) =>
    (element: UIRenderableElement): boolean =>
        element.section === section;

export const getAdminRights = (elements: UIElement[]) => {
    const granularRights = elements.filter(
        isRenderableElement,
    ) as GranularRight[];

    return granularRights.filter(isInSection('administrator'));
};

export const nestedCheckboxKeys = (element: HTMLElement | null) => {
    if (!element) return [];
    const elements = element?.querySelectorAll("input[type='checkbox']");
    if (!element || !elements) return [];

    const keys: string[] = [];
    elements.forEach((element_) => {
        const name = element_.getAttribute('name');

        if (name !== element.getAttribute('name')) {
            keys.push((name ?? '') as string);
        }
    });

    return keys;
};

/** Helper util to collect the name attribute from checkboxes in a given list of sections */
export const inputNamesInSections = (sections: string[]) => {
    const reactRoot = getReactRoot();
    const htmlSections = sections
        .map((name) => `[aria-label='${name}']`)
        .map((selector) => reactRoot.querySelector(selector))
        .filter(Boolean) as Element[];

    return htmlSections
        .flatMap((element) => getCheckboxes(element))
        .map(getAttribute('name'))
        .filter(Boolean) as string[];
};

const DELIMETER = '.';

export const fullRightCreate = (
    base: string,
    category: string,
    action: string,
) => [base, category, action].join(DELIMETER);

export const fullRightIsValid = (name: string) => {
    const [base, category, action] = name.split('.');

    return base === 'rights' && category && action;
};

export const fullRightParse = (name: string) => {
    const [base, category, action] = name.split('.');

    return {action, base, category};
};

/** Take a full right as an input */
export const isCanView = (fullRight: string) => {
    if (!fullRightIsValid(fullRight)) return false;

    const {action} = fullRightParse(fullRight);

    return action === 'can_view';
};
